import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X } from "lucide-react";

const NAV_LINKS = [
  { label: "Home", href: "#top" },
  { label: "Why We Exist", href: "#why-we-exist" },
  { label: "Who We Serve", href: "#who-we-serve" },
  { label: "Preview", href: "#preview" },
  { label: "Founding Members", href: "#founding-members" },
];

const LOGO_SRC =
  "https://customer-assets-gfyr7b9c.emergentagent.net/job_tfc-preview/artifacts/8wo6q5t4_64C5E782-E3B8-466C-A3C1-CAE785C8E0A5.jpg";

const Wordmark = () => (
  <a
    href="#top"
    data-testid="nav-logo"
    aria-label="The Football Collective — home"
    className="group flex items-center select-none"
  >
    <img
      src={LOGO_SRC}
      alt="The Football Collective"
      className="h-14 md:h-16 w-auto mix-blend-screen transition-transform duration-500 group-hover:scale-[1.04]"
      draggable="false"
    />
  </a>
);

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <>
      <motion.header
        data-testid="navbar"
        initial={{ y: -40, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1], delay: 0.1 }}
        className={`fixed top-0 left-0 right-0 z-50 transition-colors duration-500 ${
          scrolled
            ? "bg-[#0B0B0B]/70 backdrop-blur-xl border-b border-[#2A2A2A]"
            : "bg-transparent border-b border-transparent"
        }`}
      >
        <nav className="max-w-[1400px] mx-auto px-6 md:px-12 lg:px-16 h-[76px] flex items-center justify-between">
          <Wordmark />

          <div className="hidden lg:flex items-center gap-10">
            {NAV_LINKS.map((link) => (
              <a
                key={link.label}
                href={link.href}
                data-testid={`nav-link-${link.label.toLowerCase().replace(/\s+/g, "-")}`}
                className="group relative font-body text-sm font-medium tracking-wide text-[#B3B3B3] hover:text-white transition-colors duration-300"
              >
                {link.label}
                <span className="absolute -bottom-1.5 left-0 h-px w-0 bg-[#1F7A4D] transition-all duration-300 group-hover:w-full" />
              </a>
            ))}
          </div>

          <div className="flex items-center gap-3">
            <a
              href="#kick-off"
              data-testid="nav-cta-kick-off"
              className="hidden sm:inline-flex items-center gap-2 rounded-full bg-[#1F7A4D] px-5 py-2.5 font-body text-sm font-semibold text-white transition-[background-color,transform] duration-300 hover:bg-[#26945D] hover:scale-[1.03] active:scale-100"
            >
              Kick Off With Us
            </a>
            <button
              type="button"
              data-testid="nav-mobile-toggle"
              aria-label="Toggle menu"
              onClick={() => setOpen((v) => !v)}
              className="lg:hidden inline-flex h-11 w-11 items-center justify-center rounded-full border border-[#2A2A2A] text-white transition-colors duration-300 hover:border-[#1F7A4D]"
            >
              {open ? <X size={20} /> : <Menu size={20} />}
            </button>
          </div>
        </nav>
      </motion.header>

      <AnimatePresence>
        {open && (
          <motion.div
            data-testid="mobile-menu"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="fixed inset-0 z-40 bg-[#0B0B0B]/95 backdrop-blur-2xl lg:hidden"
          >
            <div className="flex h-full flex-col justify-center px-8 gap-2">
              {NAV_LINKS.map((link, i) => (
                <motion.a
                  key={link.label}
                  href={link.href}
                  onClick={() => setOpen(false)}
                  data-testid={`mobile-link-${link.label.toLowerCase().replace(/\s+/g, "-")}`}
                  initial={{ y: 30, opacity: 0 }}
                  animate={{ y: 0, opacity: 1 }}
                  transition={{
                    delay: 0.08 * i,
                    duration: 0.6,
                    ease: [0.16, 1, 0.3, 1],
                  }}
                  className="font-display text-5xl leading-none tracking-wide text-white border-b border-[#2A2A2A] py-5"
                >
                  {link.label}
                </motion.a>
              ))}
              <a
                href="#kick-off"
                onClick={() => setOpen(false)}
                data-testid="mobile-cta-kick-off"
                className="mt-8 inline-flex items-center justify-center rounded-full bg-[#1F7A4D] px-6 py-4 font-body text-base font-semibold text-white transition-colors duration-300 hover:bg-[#26945D]"
              >
                ⚽ Kick Off With Us
              </a>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
